import { useEffect, useState } from 'preact/hooks';
import {
  getRecord,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  type RecordRow,
} from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { humanizeValue } from '../lib/fieldDisplay';
import {
  AA,
  LINK,
  auditionAge,
  ageWords,
  creditEnds,
  creditsForPerson,
  inRead,
  otherEnd,
  otherEnds,
  positionOf,
  type CreditEnd,
} from '../lib/auditionSpread';
import './spread.css';

// W2 — ONE PERSON: everything this household has actually done with them
// (decision `9753e5ed`).
//
// Started as two questions; B22 (2026-09-27) added a third, because a
// casting director's page with no way to reach them was a real gap Huan
// found on the demo walk:
//
//   WHAT HAVE THEY DONE — every `aa_crew_credit`, the position beside the
//   production. This is the person↔project position vocabulary the coach spec
//   deferred, made visible; nothing here is a field on the person.
//
//   WHEN HAVE WE MET THEM — every audition of this household's that names
//   them (`aa_audition.people`, decision `deeb6266`).
//
//   HOW DO WE REACH THEM — the tier ("Worked with" / "On paperwork only"),
//   email/phone with copy (no mailto:/tel:), and the household's own notes
//   as a bio. All three read straight off the `aa_person` row itself, never
//   fetched.
//
// ⚠ BOTH LISTS ARE THIS HOUSEHOLD'S OWN, AND THERE IS NO OTHER KIND. The
// credits are the tenant's rows; the auditions are the tenant's auditions,
// read through the ordinary gated door. A cross-family view of one casting
// director — a profile, a count, a reputation — is refused by ruling and is
// not available on this screen to build out of.
//
// ⚠ THIS SCREEN READS AT 'staff'. `aa_person` and `aa_crew_credit` both
// declare `min_role_read: 'staff'` (an agent's phone number is not a child's
// screen), and the People tab is on the ADULTS' rail alone — a child's rail
// is Today · Auditions · Materials. So a refusal here is a genuinely odd
// state, and it is shown as the refusal it is rather than as an empty person.

export interface HouseholdPersonProps {
  tenantId: string;
  id: string;
  backLabel: string;
  onBack: () => void;
  onOpenAudition: (auditionId: string) => void;
  onOpenProject: (projectId: string) => void;
}

interface Around {
  loaded: boolean;
  credits: CreditEnd[];
  projectById: Map<string, RecordRow>;
  /** This household's auditions that name this person, newest first. */
  reads: RecordRow[];
  /** aa_audition's own name is `{modality} — {submitted_at}` — a machine
   * string. Every other read list names the PART and falls back; so does this. */
  roleNameByAudition: Map<string, string>;
}

const EMPTY_AROUND: Around = {
  loaded: false,
  credits: [],
  projectById: new Map(),
  reads: [],
  roleNameByAudition: new Map(),
};

export function HouseholdPerson({
  tenantId,
  id,
  backLabel,
  onBack,
  onOpenAudition,
  onOpenProject,
}: HouseholdPersonProps) {
  const [person, setPerson] = useState<RecordRow | null>(null);
  const [around, setAround] = useState<Around>(EMPTY_AROUND);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();
  // B22 §2 — the copyAddress shape from HouseholdHome.tsx: the button's own
  // label flips to "Copied"; a failed write leaves it alone, and the text
  // itself is still there to select. Two independent flags — copying the
  // email must not flip the phone button's label, and vice versa.
  const [emailCopied, setEmailCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);

  async function copyValue(value: string, setCopied: (v: boolean) => void) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      setAround((prev) => ({ ...prev, loaded: false }));
      try {
        const [record, credits, projects, auds, allRoles] = await Promise.all([
          getRecord(tenantId, id),
          listAllRecordsWithLinks(tenantId, AA.CREW_CREDIT, {
            linkTypes: [LINK.CREDIT_PERSON, LINK.CREDIT_PROJECT],
          }),
          listAllRecordsByType(tenantId, AA.PROJECT),
          listAllRecordsWithLinks(tenantId, AA.AUDITION, {
            linkTypes: [LINK.AUDITION_PEOPLE, LINK.AUDITION_CASTING_OFFICE, LINK.AUDITION_ROLE],
          }),
          listAllRecordsByType(tenantId, AA.ROLE),
        ]);
        if (cancelled) return;

        const ends = creditEnds(activeOnly(credits.records), credits.linksByRecordId);
        const reads = activeOnly(auds.records)
          .filter((a) => inRead(auds.linksByRecordId[a.id] ?? [], a.id, id))
          // Newest first — the read a parent is asking about is nearly always
          // the last one. `submitted_at` where the pack has it, falling back
          // to when the record arrived, the same rule personStanding sorts by
          // in the other direction.
          .sort((a, b) => readWhen(b) - readWhen(a));

        const roleName = new Map(activeOnly(allRoles).map((r) => [r.id, r.display_name]));
        const roleNameByAudition = new Map<string, string>();
        for (const a of reads) {
          const roleId = otherEnd(auds.linksByRecordId[a.id] ?? [], a.id, LINK.AUDITION_ROLE);
          const named = roleId ? roleName.get(roleId) : undefined;
          if (named) roleNameByAudition.set(a.id, named);
        }

        setPerson(record);
        setAround({
          loaded: true,
          credits: creditsForPerson(ends, id),
          projectById: new Map(activeOnly(projects).map((p) => [p.id, p])),
          reads,
          roleNameByAudition,
        });
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setPerson(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, retryNonce, recordsVersion]);

  if (failure) {
    return (
      <div class="sp">
        <div class="sp__crumb">
          <button type="button" onClick={onBack}>
            {backLabel}
          </button>
        </div>
        <div class="sp__say">
          {failure.headline}
          <br />
          <span class="sp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read this person again
          </button>
        ) : null}
      </div>
    );
  }

  if (!person) {
    return (
      <div class="sp">
        <div class="sp__crumb">
          <button type="button" onClick={onBack}>
            {backLabel}
          </button>
        </div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const role = person.fields.person_role;
  const company = person.fields.company;
  const heroLine = [
    typeof role === 'string' && role ? humanizeValue(role) : null,
    typeof company === 'string' && company ? company : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // B22 §1 — HOW THIS HOUSEHOLD KNOWS THEM. The tier is the point of the
  // type (recordTypes.ts's own comment on aa_person): 'researched' is a
  // name off a breakdown, 'worked_with' is someone the household has
  // actually dealt with. Said in words, never colour alone.
  const tier = person.fields.tier;
  const tierLine =
    tier === 'worked_with' ? 'Worked with' : tier === 'researched' ? 'On paperwork only' : null;

  // B22 §2 — REACH. Only the worked_with half ever carries these (the
  // schema does not enforce it — see recordTypes.ts's own note — but a
  // researched row simply will not have them).
  const email = typeof person.fields.email === 'string' ? person.fields.email : '';
  const phone = typeof person.fields.phone === 'string' ? person.fields.phone : '';

  // B22 §3 — WHAT YOU KNOW. The household's own note about the person —
  // split on blank lines into paragraphs, shown only when there is one.
  const notesRaw = typeof person.fields.notes === 'string' ? person.fields.notes : '';
  const notesParagraphs = notesRaw
    .split(/\r?\n\s*\r?\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  return (
    <div class="sp">
      <div class="sp__crumb">
        <button type="button" onClick={onBack}>
          {backLabel}
        </button>
        <span class="sp__crumb-sep">›</span>
        <span class="sp__crumb-cur">{person.display_name}</span>
      </div>

      <div class="sp__grid">
        <div class="sp__hero">
          <div class="sp__hero-top">
            <div>
              <div class="sp__title">{person.display_name}</div>
              {heroLine ? <div class="sp__sub">{heroLine}</div> : null}
              {tierLine ? <div class="sp__sub sp__tier">{tierLine}</div> : null}
            </div>
            {/* MANUAL EDIT, ALL TIERS. The schema-driven editor is the same
                door the registry always used — nothing about keeping your own
                people is behind a plan. */}
            <button
              type="button"
              class="sp__edit"
              onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.PERSON, id })}
            >
              Edit
            </button>
          </div>

          {/* ================= REACH — B22 §2 =================
              A casting director's page with no way to reach them was the
              gap this chunk closes. Shown only when at least one of
              email/phone is set — never an empty heading. No mailto:/tel:
              links: unreliable in the shells this runs in, so the address
              is said as text and offered for copy instead. */}
          {email || phone ? (
            <>
              <div class="sp__sec">
                <span>Reach</span>
              </div>
              {email ? (
                <div class="sp__reach-row">
                  <span class="sp__reach-v">{email}</span>
                  <button
                    type="button"
                    class="sp__reach-copy"
                    onClick={() => void copyValue(email, setEmailCopied)}
                  >
                    {emailCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ) : null}
              {phone ? (
                <div class="sp__reach-row">
                  <span class="sp__reach-v">{phone}</span>
                  <button
                    type="button"
                    class="sp__reach-copy"
                    onClick={() => void copyValue(phone, setPhoneCopied)}
                  >
                    {phoneCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {/* ================= WHAT YOU KNOW — B22 §3 =================
              The household's own note about the person — the bio Huan
              asked for. Shown only when set. */}
          {notesParagraphs.length > 0 ? (
            <>
              <div class="sp__sec">
                <span>What you know</span>
              </div>
              {notesParagraphs.map((para, i) => (
                <p class="sp__notes-p" key={i}>
                  {para}
                </p>
              ))}
            </>
          ) : null}

          {/* ================= EVERY CREDIT ================= */}
          <div class="sp__sec">
            <span>Credits</span>
            {around.credits.length > 0 ? <span>{around.credits.length}</span> : null}
          </div>
          {around.credits.length === 0 ? (
            <div class="sp__empty">No positions recorded for them yet.</div>
          ) : (
            around.credits.map((end) => {
              const project = end.projectId ? around.projectById.get(end.projectId) ?? null : null;
              return (
                <button
                  type="button"
                  key={end.credit.id}
                  class="sp__row"
                  onClick={() =>
                    // The credit's own row opens the credit, not the
                    // production: this row IS the thing a parent came to
                    // correct when the sheet was photographed at an angle.
                    openRecordEditor({ mode: 'edit', typeKey: AA.CREW_CREDIT, id: end.credit.id })
                  }
                >
                  <span class="sp__row-top">
                    {/* VERBATIM. "1st AD" is what the sheet printed; a
                        formatter would render "1St Ad". */}
                    <span class="sp__row-name">{positionOf(end.credit) ?? end.credit.display_name}</span>
                    {end.credit.fields.source === 'callsheet' ? (
                      <span class="sp__row-when">from a call sheet</span>
                    ) : null}
                  </span>
                  <span class="sp__row-sub">{project ? project.display_name : 'Production not recorded'}</span>
                </button>
              );
            })
          )}
          {/* The production is reachable from the credit, but only when the
              credit HAS one — a link to nothing is the shape of a button that
              always errors. */}
          {around.credits.some((e) => e.projectId) ? (
            <div class="sp__acts">
              {around.credits
                .filter((e) => e.projectId && around.projectById.has(e.projectId))
                .slice(0, 4)
                .map((e) => (
                  <button key={e.credit.id} type="button" class="sp__act" onClick={() => onOpenProject(e.projectId!)}>
                    {around.projectById.get(e.projectId!)!.display_name}
                  </button>
                ))}
            </div>
          ) : null}
          <div class="sp__acts">
            <button
              type="button"
              class="sp__act"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: AA.CREW_CREDIT,
                  // Pre-pointed at the person whose screen this is — keyed by
                  // the pack's own relationship key, which is what RecordForm
                  // reads prefills by. A wrong key here is silently DISCARDED
                  // (the A4 defect packLinkKeys.test.ts exists over), which is
                  // why LINK.CREDIT_PERSON is used and not a literal.
                  prefillRels: { [LINK.CREDIT_PERSON]: [id] },
                  contextRecord: { typeKey: AA.PERSON, id },
                })
              }
            >
              Add a credit
            </button>
          </div>
        </div>

        {/* ================= THE READS ================= */}
        <div class="sp__aside">
          <div class="sp__sec">
            <span>Auditions</span>
            {around.reads.length > 0 ? <span>{around.reads.length}</span> : null}
          </div>
          {!around.loaded ? (
            <div class="sp__empty">One moment…</div>
          ) : around.reads.length === 0 ? (
            <div class="sp__empty">No reads name them yet.</div>
          ) : (
            around.reads.map((a) => (
              <button type="button" key={a.id} class="sp__row" onClick={() => onOpenAudition(a.id)}>
                <span class="sp__row-top">
                  <span class="sp__row-name">{around.roleNameByAudition.get(a.id) ?? a.display_name}</span>
                  <span class="sp__row-when">{ageWords(auditionAge(a)) ?? ''}</span>
                </span>
                {a.lifecycle_state ? <span class="sp__row-sub">{humanizeValue(a.lifecycle_state)}</span> : null}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/** When a read went out, for ordering only. `submitted_at` where the pack has
 * it, else when the record arrived; an unplaceable read sorts last rather than
 * first, which is the only honest place for "we do not know when". */
function readWhen(a: RecordRow): number {
  const raw = typeof a.fields.submitted_at === 'string' ? a.fields.submitted_at : a.created_at;
  const t = raw ? Date.parse(raw) : Number.NaN;
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
}
